// Phase T3.1 — Extracted-slip social share card (pure).
//
// Advancement_v3_0 §T3.1. The slip came from social media; the
// extracted version goes back. A branded, shareable 1080×1080
// artefact — an organic growth loop, no ad budget.
//
// N15 (binding) — shared cards NEVER show model internals: no pWin,
// no confidence bands, no driver detail. Only the public-facing
// output: the kept legs, the combined odds, and the trim headline
// ("Trimmed from 3,000x to 180x"). This module STRIPS internals at
// construction and a guard asserts it — there is no path to leaking
// proprietary intelligence onto an exported card.
//
// Zero I/O. Produces the model + a deterministic 1080×1080 SVG
// (browser-free, fully testable). PNG rasterisation is a thin
// client/edge wrapper around this SVG.

import type { ScoredLeg } from "./legScorer.ts";

export interface ShareCardLeg {
  fixture: string;
  market: string;
  selection: string;
  /** Public odds only — the figure already on the user's slip. */
  odds: number;
}

export interface ShareCardModel {
  legs: ShareCardLeg[];
  sourceCombinedOdds: number | null;
  extractedCombinedOdds: number | null;
  /** "Trimmed from 3,000x to 180x" — the legible value prop. */
  trimHeadline: string;
  badge: string;
  link: string;
  /** Optional, caller-supplied issue date (e.g. "24 May 2026"). Kept
   *  OUT of the pure builder so the SVG stays deterministic for a given
   *  model — the caller decides the date, not the renderer. Public. */
  issuedDate?: string;
}

// Receipt document: fixed 1080 width (SHARE_DIM), portrait height grows
// with the leg count.
const SHARE_DIM = 1080;
const SANS = "'Helvetica Neue',Arial,sans-serif";
const MONO = "'SFMono-Regular',Consolas,monospace";

function fmtOdds(n: number | null): string {
  if (typeof n !== "number" || !Number.isFinite(n) || n <= 0) return "—";
  return `${Math.round(n).toLocaleString()}x`;
}

function esc(s: string): string {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** N15 — the ONLY fields allowed onto a share card. Anything else
 *  (pWin, pWin95CI, expectedValue, confidence, drivers, vetoes) is
 *  dropped here at the boundary. Pure. */
export function buildShareCardModel(input: {
  keptLegs: ScoredLeg[];
  sourceCombinedOdds: number | null;
  extractedCombinedOdds: number | null;
  link?: string;
  issuedDate?: string;
}): ShareCardModel {
  const legs: ShareCardLeg[] = (input.keptLegs ?? []).map((l) => ({
    fixture: [l.home_team, l.away_team].filter(Boolean).join(" v ") || "—",
    market: String(l.market ?? "?"),
    selection: String(l.selection ?? "?"),
    odds: typeof l.odds === "number" ? l.odds : 0,
  }));

  const src = input.sourceCombinedOdds;
  const ext = input.extractedCombinedOdds;
  const trimHeadline = (typeof src === "number" && typeof ext === "number" &&
      src > ext)
    ? `Trimmed from ${fmtOdds(src)} to ${fmtOdds(ext)}`
    : ext !== null
    ? `Extracted slip · ${fmtOdds(ext)}`
    : "STRIKZA extraction";

  return {
    legs,
    sourceCombinedOdds: src ?? null,
    extractedCombinedOdds: ext ?? null,
    trimHeadline,
    badge: "STRIKZA · Slip Extractor",
    link: input.link && input.link.trim().length > 0
      ? input.link.trim()
      : "strikza.app/slip-extractor",
    issuedDate: input.issuedDate && input.issuedDate.trim().length > 0
      ? input.issuedDate.trim()
      : undefined,
  };
}

const FORBIDDEN_INTERNAL_KEYS = [
  "pWin",
  "pWin95CI",
  "expectedValue",
  "confidence",
  "drivers",
  "vetoes",
];

/** N15 hard guard — throws if any model-internal field leaked into
 *  the card. Used in tests + before any export. */
export function assertShareCardClean(card: ShareCardModel): void {
  const blob = JSON.stringify(card);
  for (const k of FORBIDDEN_INTERNAL_KEYS) {
    if (new RegExp(`"${k}"`).test(blob)) {
      throw new Error(
        `N15 violation: share card leaked model internal "${k}".`,
      );
    }
  }
}

/** Deterministic, browser-free receipt-style SVG — a clean white
 *  document with the STRIKZA mark, a metadata block, a line-item table
 *  of the kept legs, and an emphasised combined-odds total. Portrait,
 *  fixed 1080 width, height grows with the leg count. Pure; the
 *  client/edge wrapper only rasterises this to PNG. */
export function shareCardSvg(card: ShareCardModel): string {
  const W = SHARE_DIM;
  const M = 80; // page margin
  const INK = "#16140f";
  const SUB = "#6f6c66";
  const HAIR = "#e7e4dd";
  const STRIPE = "#f7f6f3";
  const AMBER = "#d4a843";
  const legs = card.legs.slice(0, 12);
  const more = card.legs.length - legs.length;
  const b: string[] = [];

  // ── Brand lockup, top-right: two amber chevrons + STRIKZA wordmark
  //    (the landing-page mark). ──
  const lx = W - 300;
  const ly = 62;
  b.push(
    `<g transform="translate(${lx},${ly}) scale(1.4)">` +
      `<path d="M4 6 L20 6 L28 14 L12 14 Z" fill="${AMBER}"/>` +
      `<path d="M4 18 L20 18 L28 26 L12 26 Z" fill="${AMBER}" opacity="0.55"/>` +
      `</g>`,
  );
  b.push(
    `<text x="${lx + 54}" y="${ly + 30}" font-family="${SANS}" font-size="28" ` +
      `font-weight="700" letter-spacing="3" fill="${INK}">STRIKZA</text>`,
  );

  // ── Title ──
  b.push(
    `<text x="${M}" y="118" font-family="${SANS}" font-size="50" ` +
      `font-weight="800" fill="${INK}">Extraction</text>`,
  );

  // ── Metadata block (label / value rows) ──
  const meta: Array<[string, string]> = [];
  if (card.issuedDate) meta.push(["Issued", card.issuedDate]);
  meta.push(["Legs kept", String(card.legs.length)]);
  if (card.sourceCombinedOdds != null) {
    meta.push(["Source odds", fmtOdds(card.sourceCombinedOdds)]);
  }
  meta.push(["Extracted odds", fmtOdds(card.extractedCombinedOdds)]);
  let y = 186;
  for (const [k, v] of meta) {
    b.push(
      `<text x="${M}" y="${y}" font-family="${SANS}" font-size="22" ` +
        `fill="${SUB}">${esc(k)}</text>`,
    );
    b.push(
      `<text x="${M + 230}" y="${y}" font-family="${SANS}" font-size="22" ` +
        `font-weight="600" fill="${INK}">${esc(v)}</text>`,
    );
    y += 36;
  }

  // ── Headline (the value prop) ──
  y += 42;
  b.push(
    `<text x="${M}" y="${y}" font-family="${SANS}" font-size="40" ` +
      `font-weight="800" fill="${INK}">${esc(card.trimHeadline)}</text>`,
  );
  y += 32;
  b.push(
    `<text x="${M}" y="${y}" font-family="${SANS}" font-size="20" ` +
      `fill="${SUB}">Risky legs trimmed &#8212; the edge kept.</text>`,
  );

  // ── Line-item table ──
  y += 58;
  b.push(
    `<text x="${M}" y="${y}" font-family="${SANS}" font-size="16" ` +
      `letter-spacing="1.5" fill="${SUB}">SELECTION</text>`,
  );
  b.push(
    `<text x="${W - M}" y="${y}" text-anchor="end" font-family="${SANS}" ` +
      `font-size="16" letter-spacing="1.5" fill="${SUB}">ODDS</text>`,
  );
  y += 14;
  b.push(`<line x1="${M}" y1="${y}" x2="${W - M}" y2="${y}" stroke="${HAIR}"/>`);

  const rowH = 66;
  const bodyTop = y + 6;
  legs.forEach((l, i) => {
    const top = bodyTop + i * rowH;
    if (i % 2 === 1) {
      b.push(
        `<rect x="${M - 24}" y="${top}" width="${W - 2 * M + 48}" ` +
          `height="${rowH}" fill="${STRIPE}"/>`,
      );
    }
    b.push(
      `<text x="${M}" y="${top + 34}" font-family="${SANS}" font-size="25" ` +
        `font-weight="600" fill="${INK}">${esc(l.fixture)}</text>`,
    );
    b.push(
      `<text x="${M}" y="${top + 56}" font-family="${SANS}" font-size="19" ` +
        `fill="${SUB}">${esc(l.market)} &#183; ${esc(l.selection)}</text>`,
    );
    b.push(
      `<text x="${W - M}" y="${top + 40}" text-anchor="end" ` +
        `font-family="${MONO}" font-size="25" fill="${INK}">` +
        `${l.odds.toFixed(2)}</text>`,
    );
  });
  let yy = bodyTop + legs.length * rowH;
  if (more > 0) {
    b.push(
      `<text x="${M}" y="${yy + 30}" font-family="${SANS}" font-size="20" ` +
        `fill="${SUB}">+ ${more} more leg${more === 1 ? "" : "s"}</text>`,
    );
    yy += 44;
  }

  // ── Totals ──
  yy += 10;
  b.push(`<line x1="${M}" y1="${yy}" x2="${W - M}" y2="${yy}" stroke="${HAIR}"/>`);
  yy += 14;
  if (card.sourceCombinedOdds != null) {
    b.push(
      `<text x="${W - M - 240}" y="${yy + 34}" font-family="${SANS}" ` +
        `font-size="22" fill="${SUB}">Original combined</text>`,
    );
    b.push(
      `<text x="${W - M}" y="${yy + 34}" text-anchor="end" ` +
        `font-family="${MONO}" font-size="22" fill="${SUB}">` +
        `${fmtOdds(card.sourceCombinedOdds)}</text>`,
    );
    yy += 40;
  }
  b.push(
    `<rect x="${M - 24}" y="${yy + 6}" width="${W - 2 * M + 48}" height="52" ` +
      `fill="${STRIPE}"/>`,
  );
  b.push(
    `<text x="${M}" y="${yy + 40}" font-family="${SANS}" font-size="24" ` +
      `font-weight="700" fill="${INK}">Extracted combined</text>`,
  );
  b.push(
    `<text x="${W - M}" y="${yy + 40}" text-anchor="end" font-family="${MONO}" ` +
      `font-size="26" font-weight="700" fill="${INK}">` +
      `${fmtOdds(card.extractedCombinedOdds)}</text>`,
  );
  yy += 58;

  // ── Footer ──
  const fy = yy + 72;
  b.push(
    `<line x1="${M}" y1="${fy - 34}" x2="${W - M}" y2="${fy - 34}" ` +
      `stroke="${HAIR}"/>`,
  );
  b.push(
    `<g transform="translate(${M},${fy - 22}) scale(0.9)">` +
      `<path d="M4 6 L20 6 L28 14 L12 14 Z" fill="${AMBER}"/>` +
      `<path d="M4 18 L20 18 L28 26 L12 26 Z" fill="${AMBER}" opacity="0.55"/>` +
      `</g>`,
  );
  b.push(
    `<text x="${M + 44}" y="${fy}" font-family="${SANS}" font-size="20" ` +
      `fill="${SUB}">${esc(card.badge)}</text>`,
  );
  b.push(
    `<text x="${W - M}" y="${fy}" text-anchor="end" font-family="${SANS}" ` +
      `font-size="20" fill="${SUB}">${esc(card.link)}</text>`,
  );

  const H = fy + 50;
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${SHARE_DIM}" ` +
    `height="${H}" viewBox="0 0 ${SHARE_DIM} ${H}">` +
    `<rect width="${SHARE_DIM}" height="${H}" fill="#ffffff"/>` +
    b.join("") +
    `</svg>`
  );
}
